import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import { motion } from "framer-motion";

// Import your logo assets
import figLogo from "../../assets/logos/fig_CROP.png";
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

// Import the improved "Software Built for K12" section
import WhatWeBuild from "./WhatWeBuild";

// Feature Cards Data — now reflecting the full product portfolio
const features = [
    {
        title: "FIG Finance",
        description: (
            <>
                <strong>USDA-compliant financials for K12 nutrition.</strong><br />
                Procurement, spending, vendor management, and audit-ready reporting — live across 8+ district deployments nationwide.
                <br /><br />
                <span style={{ color: "#208478" }}>The leading finance platform built for how school nutrition actually works.</span>
            </>
        ),
        image: figLogo,
        link: "/solutions#finance",
    },
    {
        title: "Hi5 Palm Scan & POS",
        description: (
            <>
                <strong>Biometric ID and point of sale for school cafeterias.</strong><br />
                Hi5 uses palm vein scanning for fast, hygienic student identification. Our POS handles real-time meal tracking, eligibility, and daily reporting.
                <br /><br />
                <span style={{ color: "#208478" }}>No cards. No codes. Just a wave of the hand.</span>
            </>
        ),
        image: figLogo,
        link: "/solutions#nutrition",
    },
    {
        title: "AIOS",
        description: (
            <>
                <strong>AI infrastructure for any company.</strong><br />
                Federate your databases, deploy autonomous agents, and build intelligent systems on a proven platform. Schema discovery, knowledge graphs, and continuous learning — out of the box.
                <br /><br />
                <span style={{ color: "#208478" }}>The foundation for building AI-powered applications without starting from scratch.</span>
            </>
        ),
        image: swanAiLabsLogo,
        link: "https://aios.swancreate.com",
    },
    {
        title: "Swan AI Labs",
        description: (
            <>
                <strong>Real AI for real operational results.</strong><br />
                RAG solutions, custom LLM chatbots, document AI, nutrient analysis, predictive analytics, and deep visualization — for any industry.
                <br /><br />
                <span style={{ color: "#208478" }}>Not hype. AI that solves real problems.</span>
            </>
        ),
        image: swanAiLabsLogo,
        link: "/solutions#ai",
    },
    {
        title: "FIG KIDS & Training",
        description: (
            <>
                <strong>CACFP management and K12 professional development.</strong><br />
                FIG KIDS handles daycare enrollment, meals, billing, and federal reimbursement. The FIG App LMS delivers training, CEU tracking, and compliance for nutrition teams.
                <br /><br />
                <span style={{ color: "#208478" }}>From daycare to district — complete K12 nutrition support.</span>
            </>
        ),
        image: figLogo,
        link: "/solutions#lms",
    },
    {
        title: "Custom Software",
        description: (
            <>
                <strong>Your dev team without the overhead.</strong><br />
                Full-scale enterprise software, CRM, secure APIs, and data pipelines for any industry. We design, build, and support platforms that fit your vision.
                <br /><br />
                <span style={{ color: "#208478" }}>EdTech, commercial, or anything else — we build it.</span>
            </>
        ),
        image: swanSolutionsLogo,
        link: "/solutions#commercial",
    },
];

export default function Home() {
    // Haptic animation config for motion
    const haptic = {
        whileTap: { scale: 0.96 },
        whileHover: { scale: 1.03 },
        transition: { type: "spring", stiffness: 330, damping: 16 },
    };

    return (
        <Box sx={{ width: "100%", bgcolor: "background.default" }}>
            {/* Hero Section */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 16 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background:
                        "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "70vh", md: "90vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 10,
                }}
            >
                <Box
                    component="img"
                    src={swanSolutionsLogo}
                    alt="Swan Solutions Logo"
                    sx={{
                        height: { xs: 80, md: 140 },
                        mb: 2,
                        filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.15))",
                    }}
                />
                <Typography
                    variant="h1"
                    gutterBottom
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.5rem", md: "4rem" },
                        letterSpacing: "-0.04em",
                        mb: 1,
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    Swan Solutions
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 4,
                        fontWeight: 700,
                        fontSize: { xs: "1.18rem", md: "1.65rem" },
                        maxWidth: 800,
                        mx: "auto",
                        textShadow: "0 2px 14px rgba(20,92,140,0.13)",
                    }}
                >
                    K12 Technology Partner
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 700,
                        mx: "auto",
                        mb: 4,
                        fontWeight: 400,
                        fontSize: { xs: "1rem", md: "1.17rem" },
                        color: "rgba(255,255,255,0.97)",
                    }}
                >
                    We build modern, practical software — EdTech platforms for K12 districts and commercial products for any business. Finance. Nutrition. AI. Custom software.
                </Typography>
                <Button
                    component={motion.a}
                    href="/solutions"
                    size="large"
                    variant="contained"
                    color="secondary"
                    {...haptic}
                    sx={{
                        fontSize: "1.08rem",
                        fontWeight: 700,
                        py: 1.5,
                        px: 4,
                        borderRadius: 99,
                        boxShadow: "0 3px 14px rgba(44,153,200,0.12)",
                        mt: 1,
                    }}
                >
                    Explore Our Solutions
                </Button>
            </Box>

            {/* Who We Are */}
            <Container
                maxWidth="lg"
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.7 }}
                sx={{ textAlign: "center", mb: 8 }}
            >
                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ fontWeight: 800, fontSize: { xs: "2.2rem", md: "2.8rem" } }}
                >
                    Who We Are
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 820,
                        mx: "auto",
                        mb: 3,
                        fontSize: { xs: "1.07rem", md: "1.15rem" },
                        fontWeight: 400,
                    }}
                >
                    Swan Solutions is a K12 software company with deep expertise in school nutrition, operations, and compliance. We deliver platforms and integrations that simplify complex processes, automate reporting, and empower leaders to make strategic, data-driven decisions.
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 820,
                        mx: "auto",
                        fontSize: { xs: "1.07rem", md: "1.13rem" },
                        fontWeight: 400,
                    }}
                >
                    Our clients include districts of every size, plus vendors, service providers, and EdTech partners. From flagship products like FIG Finance to complete custom builds, we modernize, streamline, and help you better serve your communities.
                </Typography>
            </Container>

            {/* Core Solutions Logos */}
            <Container maxWidth="xl" sx={{ mb: 10 }}>
                <Typography
                    variant="h3"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 800,
                        mb: 4,
                        fontSize: { xs: "1.8rem", md: "2.5rem" },
                    }}
                >
                    Our Core Solutions
                </Typography>
                <Grid
                    container
                    spacing={4}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mt: 4 }}
                >
                    {[figLogo, swanAiLabsLogo, swanSolutionsLogo].map((logo, i) => (
                        <Grid item key={i}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(255,255,255,0.8)",
                                    boxShadow: "0 4px 28px 0 rgba(60, 120, 150, 0.10)",
                                    border: "2.5px solid #e7f7f7",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: { xs: 120, md: 160 },
                                    width: { xs: 120, md: 160 },
                                    overflow: "visible",
                                    m: { xs: 1.5, md: 2.5 },
                                    transition: "box-shadow 0.2s",
                                    "&:hover": {
                                        boxShadow: "0 8px 36px 0 #21dbb87e",
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={logo}
                                    alt={`Partner logo ${i + 1}`}
                                    sx={{
                                        maxHeight: { xs: 90, md: 120 },
                                        maxWidth: { xs: 90, md: 120 },
                                        objectFit: "contain",
                                        borderRadius: "8px",
                                        background: "transparent",
                                        display: "block",
                                        m: "auto",
                                        p: 0,
                                    }}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Product Cards */}
            <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
                <Grid container spacing={5} justifyContent="center">
                    {features.map((sol, i) => (
                        <Grid key={sol.title} item xs={12} md={6} lg={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                {...haptic}
                                transition={{ duration: 0.5 + i * 0.09 }}
                                style={{ height: "100%" }}
                            >
                                <Card
                                    elevation={3}
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        borderRadius: 4,
                                        px: 2,
                                        py: 3,
                                        boxShadow: "0 3px 28px 0 rgba(20,76,100,0.07)",
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={sol.image}
                                        alt={`${sol.title} logo`}
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            objectFit: "contain",
                                            borderRadius: 2,
                                            bgcolor: "#e7f5f6",
                                            mb: 2,
                                            mt: 1,
                                        }}
                                    />
                                    <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 2,
                                                color: "primary.main",
                                                fontSize: "1.25rem",
                                            }}
                                        >
                                            {sol.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ color: "text.primary", mb: 2, fontSize: "1.09rem" }}
                                        >
                                            {sol.description}
                                        </Typography>
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            component={motion.a}
                                            href={sol.link}
                                            size="large"
                                            variant="contained"
                                            {...haptic}
                                            sx={{
                                                fontWeight: 700,
                                                borderRadius: 99,
                                                px: 4,
                                                fontSize: "1.05rem",
                                                background: "linear-gradient(90deg,#145c8c,#21dbb8)",
                                                color: "#fff",
                                            }}
                                        >
                                            Learn More
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* --- Improved "Software Built for K12" Section --- */}
            <WhatWeBuild />

            {/* AIOS Product Highlight */}
            <Container maxWidth="md" sx={{ mb: 10 }}>
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <Card
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "center",
                            p: { xs: 2, md: 4 },
                            borderRadius: 4,
                            boxShadow: 6,
                            bgcolor: "#e9f8fa",
                        }}
                    >
                        <Box
                            component="img"
                            src={swanAiLabsLogo}
                            alt="AIOS"
                            sx={{
                                width: { xs: 120, md: 180 },
                                height: "auto",
                                mb: { xs: 2, md: 0 },
                                mr: { md: 4 },
                                borderRadius: 2,
                                boxShadow: "0 1px 8px 0 rgba(40,140,190,0.08)",
                                bgcolor: "#fff",
                                p: 1,
                            }}
                        />
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 800,
                                    mb: 1,
                                    letterSpacing: "-0.01em",
                                    color: "#207c8f",
                                }}
                            >
                                AIOS — AI Infrastructure for Any Company
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1.5 }}>
                                The same Agentic Operating System that powers every Swan product is now available for your organization. Federate your databases, deploy AI agents, and build intelligent systems on a proven platform.
                            </Typography>
                            <Button
                                component={motion.a}
                                href="https://aios.swancreate.com"
                                variant="outlined"
                                size="large"
                                {...haptic}
                                sx={{
                                    fontWeight: 700,
                                    borderRadius: 99,
                                    px: 4,
                                    fontSize: "1.05rem",
                                    borderColor: "#21dbb8",
                                    color: "#13847d",
                                    "&:hover": {
                                        borderColor: "#13847d",
                                        background: "#d9fcf3",
                                    },
                                }}
                            >
                                Explore AIOS
                            </Button>
                        </Box>
                    </Card>
                </motion.div>
            </Container>
        </Box>
    );
}
